import React, { useEffect, useState, useRef } from 'react';
import { alertsAPI, sitesAPI } from '../services/api';
import { useAuth } from '../context/AuthContext';
import { useTheme, T } from '../context/ThemeContext';
import MokedPage from './MokedPage';

// Landing screen for a site_manager ("סייר") scoped to exactly one site: instead of the
// full multi-site מוקד (map + sidebar, built for someone watching many locations at
// once), this shows only that one site's floor plan(s), full-screen, with live blinking
// pins wherever an alert currently is - no map clutter that doesn't make sense when
// there's only ever one place to look. Reuses the exact same alert-handling (ack/resolve
// with required notes) and alarm-sound logic as MokedPage.jsx, just without its map.
//
// Falls back to rendering the normal <MokedPage/> as-is whenever this simplified view
// wouldn't actually be useful: more than one assigned site (this guard view only makes
// sense for exactly one), or that one site has no floor plan uploaded yet (per Eran,
// 2026-07-28 - "if there's no floor plan, give him the regular מוקד operator interface",
// not a broken/empty screen).
export default function GuardPage() {
  const { user, logout } = useAuth();
  const { dark, setDark, lang, setLang } = useTheme();
  const t = T[lang];
  const rtl = lang === 'he';

  const [sites, setSites] = useState(null); // null = still loading
  useEffect(() => {
    sitesAPI.getAll().then(r => setSites(r.data)).catch(() => setSites([]));
  }, []);

  if (sites === null) return <div style={{padding:40,textAlign:'center',color:'#888'}}>{t.loading}</div>;

  const hasSingleFloorplanSite = sites.length === 1 && (sites[0].floorplans || []).length > 0;
  if (!hasSingleFloorplanSite) return <MokedPage />;

  return <SingleSiteGuardView site={sites[0]} user={user} logout={logout}
    dark={dark} setDark={setDark} lang={lang} setLang={setLang} t={t} rtl={rtl} />;
}

function SingleSiteGuardView({ site, user, logout, dark, setDark, lang, setLang, t, rtl }) {
  const [alerts, setAlerts] = useState([]);
  const [selected, setSelected] = useState(null);
  const [notes, setNotes] = useState('');
  const [showNotes, setShowNotes] = useState(false);
  const [loading, setLoading] = useState(false);
  const [mutedIds, setMutedIds] = useState(() => new Set());
  const [quickAlert, setQuickAlert] = useState(null);

  const c = dark ? {
    bg:'#060d1a', topbar:'#0a1628', card:'#0a1628', border:'#1e3a5f',
    text:'#fff', sub:'#94a3b8', stat:'#0a1628', modal:'#0f1f38', notes:'#060d1a',
    closeBg:'#1a2e4a', closeTxt:'#cbd5e1',
  } : {
    bg:'#f0f4f8', topbar:'#1e40af', card:'#ffffff', border:'#bfdbfe',
    text:'#1e293b', sub:'#475569', stat:'#ffffff', modal:'#ffffff', notes:'#f8fafc',
    closeBg:'#e2e8f0', closeTxt:'#475569',
  };

  const ST = {
    new:          { label: rtl ? 'חדש'          : 'New',          color: '#ef4444' },
    acknowledged: { label: rtl ? 'בטיפול'       : 'Acknowledged', color: '#eab308' },
    in_treatment: { label: rtl ? 'בטיפול מתקדם' : 'In Treatment', color: '#3b82f6' },
  };
  const statusMeta = (status) => ST[status] || { label: rtl ? 'אין אירוע פעיל' : 'No active incident', color: '#3b82f6' };

  useEffect(() => {
    // /api/alerts/active is already scoped server-side to this user's assigned site(s)
    // (siteManagerSiteIds in api.js) - a site_manager with one site only ever gets that
    // one site's alerts back, no extra client-side filtering needed.
    const load = () => alertsAPI.getActive().then(r => setAlerts(r.data)).catch(() => {});
    load();
    const iv = setInterval(load, 3000);
    return () => clearInterval(iv);
  }, []);

  // Same persistent-AudioContext alarm as MokedPage.jsx - repeats as long as any
  // unmuted 'new' alert exists, never stops on a timer alone.
  const audioCtxRef = useRef(null);
  const getAudioCtx = () => {
    if (!audioCtxRef.current) audioCtxRef.current = new (window.AudioContext || window.webkitAudioContext)();
    if (audioCtxRef.current.state === 'suspended') audioCtxRef.current.resume();
    return audioCtxRef.current;
  };
  const playAlarm = () => {
    try {
      const ctx = getAudioCtx();
      const dur = 1.2;
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.type = 'sawtooth';
      gain.gain.value = 0.28;
      osc.connect(gain);
      gain.connect(ctx.destination);
      const now = ctx.currentTime;
      for (let time = 0; time < dur; time += 0.15) {
        osc.frequency.setValueAtTime((time / 0.15) % 2 < 1 ? 1250 : 850, now + time);
      }
      osc.start(now);
      osc.stop(now + dur);
    } catch (e) {}
  };
  useEffect(() => {
    const hasUnmutedNew = alerts.some(a => a.status === 'new' && !mutedIds.has(a.id));
    if (!hasUnmutedNew) return;
    playAlarm();
    const iv = setInterval(playAlarm, 1500);
    return () => clearInterval(iv);
  }, [alerts, mutedIds]);

  const reload = async () => {
    const r = await alertsAPI.getActive();
    setAlerts(r.data); setSelected(null); setNotes(''); setShowNotes(false);
  };

  const openManageEvent = async (alert) => {
    setQuickAlert(null);
    setNotes('');
    let current = alert;
    if (alert.status === 'new') {
      setLoading(true);
      try {
        await alertsAPI.acknowledge(alert.id);
        const fresh = await alertsAPI.getActive();
        setAlerts(fresh.data);
        current = fresh.data.find(a => a.id === alert.id) || alert;
      } catch (e) {}
      setLoading(false);
    }
    setSelected(current);
    setShowNotes(true);
  };

  const ack = async () => { setLoading(true); try { await alertsAPI.acknowledge(selected.id); await reload(); } finally { setLoading(false); } };
  const resolve = async () => {
    if (!notes.trim()) { alert(rtl ? 'יש להזין תיאור דרכי הפעולה שבוצעו לפני סגירת האירוע' : 'Please describe the actions taken before closing the event'); return; }
    setLoading(true);
    try {
      const closedId = selected.id;
      await alertsAPI.resolve(closedId, notes);
      const check = await alertsAPI.getActive();
      const stillActive = check.data.some(a => a.id === closedId);
      alert(stillActive
        ? (rtl ? 'שגיאה: האירוע לא נסגר בפועל, נסה שוב' : 'Error: the event did not actually close, please try again')
        : (rtl ? '✓ האירוע נסגר בהצלחה' : '✓ Event closed successfully'));
      await reload();
    } catch (e) {
      alert(rtl ? 'שגיאה בסגירת האירוע: ' + e.message : 'Error closing event: ' + e.message);
    } finally { setLoading(false); }
  };
  const onClose = () => { setSelected(null); setNotes(''); setShowNotes(false); };

  const btn = (onClick, bg, color, border, text, disabled) => (
    <button onClick={onClick} disabled={disabled}
      style={{background:bg, color, border:`2px solid ${border}`, borderRadius:7, padding:'9px 16px', fontSize:13, fontWeight:700, cursor:'pointer'}}>
      {text}
    </button>
  );

  const floorplans = site.floorplans || [];

  return (
    <div style={{display:'flex', flexDirection:'column', height:'100vh',
      fontFamily:'system-ui,Arial,sans-serif', background:c.bg, color:c.text, direction: rtl ? 'rtl' : 'ltr'}}>
      <style>{`*{box-sizing:border-box} @keyframes blink{0%,100%{opacity:1}50%{opacity:0.3}}`}</style>

      {/* Topbar - deliberately simpler than MokedPage's: one site, no map/reports/admin
          links a single-site guard has no use for, just identity + live status + exit. */}
      <div style={{height:54, background:c.topbar, borderBottom:`2px solid ${c.border}`,
        display:'flex', alignItems:'center', justifyContent:'space-between', padding:'0 20px', flexShrink:0, gap:10}}>
        <div style={{display:'flex', alignItems:'center', gap:10}}>
          <div style={{width:36, height:36, background:'#f97316', borderRadius:8,
            display:'flex', alignItems:'center', justifyContent:'center', fontSize:13, fontWeight:800, color:'#fff'}}>SS</div>
          <div>
            <div style={{fontSize:15, fontWeight:800, color:'#fff'}}>{site.name}</div>
            <div style={{fontSize:11, color:'#94a3b8'}}>{site.address}{site.city ? ', '+site.city : ''}</div>
          </div>
        </div>
        <div style={{display:'flex', alignItems:'center', gap:7, background:c.stat, border:`1px solid ${c.border}`, borderRadius:50, padding:'5px 16px'}}>
          <div style={{width:8, height:8, borderRadius:'50%', background:'#22c55e', animation:'blink 1.5s infinite'}}/>
          <span style={{fontSize:11, color:'#22c55e', fontWeight:700, letterSpacing:1}}>LIVE</span>
          {alerts.length > 0 && <span style={{background:'#ef4444', color:'#fff', borderRadius:6, padding:'2px 8px', fontSize:11, fontWeight:700, marginInlineStart:6}}>🚨 {alerts.length}</span>}
        </div>
        <div style={{display:'flex', alignItems:'center', gap:8}}>
          <button onClick={() => setLang(rtl ? 'en' : 'he')} style={{background:c.stat, border:`1px solid ${c.border}`, color:c.text, borderRadius:6, padding:'5px 11px', fontSize:12, cursor:'pointer', fontWeight:700}}>{rtl ? 'EN' : 'עב'}</button>
          <button onClick={() => setDark(!dark)} style={{background:c.stat, border:`1px solid ${c.border}`, color:c.text, borderRadius:6, padding:'5px 11px', fontSize:14, cursor:'pointer'}}>{dark ? '☀️' : '🌙'}</button>
          <button onClick={() => { localStorage.removeItem('semsim_token'); window.location.href = '/'; }} style={{background:'transparent', border:`1px solid ${c.border}`, color:'#64748b', borderRadius:6, padding:'5px 10px', fontSize:11, cursor:'pointer'}}>{t.logout}</button>
        </div>
      </div>

      {/* Alert strip - covers any active alert regardless of whether it has a floor-plan
          pin (older buttons without a floorplan assignment are still fully manageable
          here, not just the ones with a visual pin below). */}
      {alerts.length > 0 && (
        <div style={{display:'flex', gap:8, padding:'10px 16px', overflowX:'auto', flexShrink:0, borderBottom:`2px solid ${c.border}`}}>
          {alerts.map(a => {
            const s = ST[a.status] || ST.new;
            return (
              <div key={a.id} onClick={() => setQuickAlert(a)}
                style={{flexShrink:0, cursor:'pointer', background:c.card, border:`2px solid ${s.color}`, borderRadius:8,
                  padding:'8px 14px', minWidth:180}}>
                <div style={{display:'flex', justifyContent:'space-between', gap:10}}>
                  <span style={{fontSize:12, fontWeight:700, color:c.text}}>{a.button_label || (rtl?'כפתור':'Button')}</span>
                  <span style={{fontSize:10, background:s.color, color:'#fff', borderRadius:4, padding:'1px 6px', fontWeight:700}}>{s.label}</span>
                </div>
                <div style={{fontSize:12, color:s.color, marginTop:2, fontWeight:800, fontVariantNumeric:'tabular-nums'}}>
                  🕐 {new Date(a.triggered_at).toLocaleTimeString(rtl ? 'he-IL' : 'en-US', {hour:'2-digit',minute:'2-digit',second:'2-digit'})}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Floor plans - the primary view. Every floor shown together (no per-floor tabs,
          same as the modal elsewhere in this project), with a blinking pin for every
          currently-active alert positioned on it. */}
      <div style={{flex:1, overflowY:'auto', padding:20}}>
        <div style={{display:'flex', flexDirection:'column', gap:20, maxWidth:900, margin:'0 auto'}}>
          {floorplans.map(fp => {
            const pinsHere = alerts.filter(a => a.floorplan_id === fp.id && a.pin_x != null && a.pin_y != null);
            return (
              <div key={fp.id} style={{background:c.card, border:`1px solid ${c.border}`, borderRadius:10, overflow:'hidden'}}>
                <div style={{padding:'8px 14px', fontSize:13, fontWeight:700, color:c.sub, borderBottom:`1px solid ${c.border}`}}>🖼 {fp.label}</div>
                <div style={{position:'relative', display:'block'}} onClick={() => { if (pinsHere.length === 1) setQuickAlert(pinsHere[0]); }}>
                  <img src={fp.image_path} alt={fp.label} style={{display:'block', width:'100%'}}/>
                  {pinsHere.map(a => {
                    const s = ST[a.status] || ST.new;
                    return (
                      <div key={a.id} onClick={(e) => { e.stopPropagation(); setQuickAlert(a); }}
                        title={a.button_label}
                        style={{position:'absolute', left:`${parseFloat(a.pin_x)*100}%`, top:`${parseFloat(a.pin_y)*100}%`,
                          transform:'translate(-50%,-50%)', width:32, height:32, borderRadius:'50%', background:s.color,
                          border:'3px solid #fff', boxShadow:`0 0 14px ${s.color}`, cursor:'pointer',
                          animation: a.status==='new' ? 'blink 1s infinite' : 'none',
                          display:'flex', alignItems:'center', justifyContent:'center', fontSize:15}}>🚨</div>
                    );
                  })}
                </div>
              </div>
            );
          })}
          {alerts.length === 0 && (
            <div style={{textAlign:'center', color:c.sub, padding:'30px 0', fontSize:13}}>{t.noAlerts}</div>
          )}
        </div>
      </div>

      {/* Quick action card - mute/unmute + open the full modal, same two-step flow as
          MokedPage.jsx's quickAlert card (clicking a pin/strip item lands here first,
          not straight in the full modal). */}
      {quickAlert && (
        <div style={{position:'fixed', bottom:24, insetInlineEnd:24, zIndex:1000,
          background:c.modal, border:`2px solid ${(ST[quickAlert.status]||ST.new).color}`,
          borderRadius:12, padding:18, width:290, boxShadow:'0 10px 34px rgba(0,0,0,0.45)'}}>
          <button onClick={()=>setQuickAlert(null)}
            style={{position:'absolute', top:8, insetInlineEnd:8, background:'transparent',
              border:'none', color:c.sub, fontSize:16, cursor:'pointer', fontWeight:700, lineHeight:1}}>×</button>
          <div style={{fontWeight:800, fontSize:14, color:c.text, marginBottom:6, paddingInlineEnd:16}}>{quickAlert.button_label || site.name}</div>
          <div style={{fontSize:12, color:c.sub, marginBottom:14}}>📍 {site.address}{site.city ? ', ' + site.city : ''}</div>
          <div style={{display:'flex', flexDirection:'column', gap:8}}>
            <button onClick={() => setMutedIds(prev => {
                const next = new Set(prev);
                if (next.has(quickAlert.id)) next.delete(quickAlert.id); else next.add(quickAlert.id);
                return next;
              })}
              style={{background:mutedIds.has(quickAlert.id)?'#374151':'#7c2d12', color:'#fed7aa',
                border:'2px solid #fb923c', borderRadius:7, padding:'9px 14px', fontSize:13,
                fontWeight:700, cursor:'pointer'}}>
              {mutedIds.has(quickAlert.id) ? (rtl?'🔊 בטל השתקה':'🔊 Unmute Sound') : (rtl?'🔇 השתק סאונד':'🔇 Mute Sound')}
            </button>
            <button onClick={() => openManageEvent(quickAlert)}
              style={{background:'#1d4ed8', color:'#fff', border:'2px solid #3b82f6',
                borderRadius:7, padding:'9px 14px', fontSize:13, fontWeight:700, cursor:'pointer'}}>
              📋 {rtl?'נהל אירוע':'Manage Event'}
            </button>
          </div>
        </div>
      )}

      {/* Manage-event modal - identical logic/markup to MokedPage.jsx's, duplicated here
          rather than extracted into a shared component (matches this codebase's existing
          per-page style, e.g. SiteFloorplansPanel isn't shared between pages either). */}
      {selected && (
        <div style={{position:'fixed', inset:0, background:'rgba(0,0,0,0.75)', display:'flex', alignItems:'center', justifyContent:'center', zIndex:1100}}
          onClick={e => e.target === e.currentTarget && onClose()}>
          <div style={{background:c.modal, border:`2px solid ${c.border}`, borderRadius:12, width:390, maxWidth:'95vw', maxHeight:'88vh', overflowY:'auto', direction: rtl ? 'rtl' : 'ltr'}}>
            <div style={{padding:'13px 18px', background:c.topbar, display:'flex', alignItems:'center', justifyContent:'space-between'}}>
              <span style={{fontSize:16, fontWeight:700, color:'#fff'}}>{selected.button_label || site.name}</span>
              <div style={{display:'flex', alignItems:'center', gap:8}}>
                <span style={{background:statusMeta(selected.status).color, color:'#fff', fontSize:11, fontWeight:700, padding:'3px 10px', borderRadius:6}}>{statusMeta(selected.status).label}</span>
                <button onClick={onClose} style={{background:c.closeBg, border:`1px solid ${c.border}`, color:'#94a3b8', width:28, height:28, borderRadius:6, cursor:'pointer', fontSize:15, fontWeight:700, lineHeight:1}}>×</button>
              </div>
            </div>
            <div style={{padding:'10px 18px'}}>
              {[
                ['🏢', [selected.floor && `${rtl?'קומה':'Floor'} ${selected.floor}`, selected.room && `${rtl?'חדר':'Room'} ${selected.room}`].filter(Boolean).join(' | ')],
                ['🍄', selected.button_type],
                ['🔑', site.entry_code && `${rtl?'קוד כניסה':'Entry code'}: ${site.entry_code}`],
                ['📝', site.site_notes],
                ['🕐', selected.triggered_at && new Date(selected.triggered_at).toLocaleTimeString(rtl?'he-IL':'en-US')],
              ].filter(([,v]) => v).map(([icon, val], i) => (
                <div key={i} style={{display:'flex', alignItems:'center', gap:10, padding:'7px 0', borderBottom:`1px solid ${c.border}`}}>
                  <span style={{fontSize:15, width:22, textAlign:'center', flexShrink:0}}>{icon}</span>
                  <span style={{fontSize:13, color:c.sub}}>{val}</span>
                </div>
              ))}
              {(site.contacts||[]).map((ct,i) => (
                <div key={ct.id||i} style={{display:'flex', alignItems:'center', gap:10, padding:'7px 0', borderBottom:`1px solid ${c.border}`}}>
                  <span style={{fontSize:15, width:22, textAlign:'center', flexShrink:0}}>👤</span>
                  <span style={{fontSize:13, color:c.sub, flex:1}}>{ct.full_name}{ct.role_label ? ` (${ct.role_label})` : ''}</span>
                  <a href={`tel:${ct.phone}`} style={{fontSize:13, color:'#3b82f6', fontWeight:700, textDecoration:'none'}}>📞 {ct.phone}</a>
                </div>
              ))}
            </div>
            {showNotes && (
              <div style={{padding:'0 18px 10px'}}>
                <textarea placeholder={t.notesPlaceholder} value={notes} onChange={e => setNotes(e.target.value)}
                  style={{width:'100%', minHeight:70, background:c.notes, border:`2px solid ${notes.trim()?c.border:'#ef4444'}`, borderRadius:8, padding:10, fontSize:13, color:c.text, fontFamily:'inherit', resize:'vertical', boxSizing:'border-box'}}/>
                {!notes.trim() && <div style={{color:'#ef4444', fontSize:11, marginTop:4}}>{rtl?'שדה חובה לפני סגירת האירוע':'Required before closing the event'}</div>}
              </div>
            )}
            <div style={{padding:'12px 18px', background:c.topbar, borderTop:`2px solid ${c.border}`, display:'flex', gap:8, flexWrap:'wrap'}}>
              {selected.status==='new' && btn(ack, '#1d4ed8','#fff','#3b82f6', t.ack, loading)}
              {showNotes && btn(resolve,'#14532d','#bbf7d0','#4ade80', t.resolve, loading || !notes.trim())}
              <button onClick={onClose} style={{background:c.closeBg, color:c.closeTxt, border:`1px solid ${c.border}`, borderRadius:7, padding:'9px 14px', fontSize:13, fontWeight:600, cursor:'pointer', marginInlineStart:'auto'}}>{t.close}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
